import { Box, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-admin';

import { createAdminApi, type AdminSyncConflictDetail } from '@/admin/adminApi';
import { formatFull } from '@/admin/audit/auditPresentation';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { SYNC_SUPERVISION_PATH } from '@/admin/synchronization/syncSupervisionApi';
import { ABSENT, formatInstant } from '@/admin/users/accountPresentation';
import { TechnicalId } from '@/admin/users/accountsUi';
import { entityTypeLabel } from '@/admin/users/accountUsagePresentation';
import { accountDetailPath } from '@/admin/users/routes';
import { useHttpClient } from '@/app/providers';
import { AccessDenied, Badge, Button, EmptyState, ErrorState, LoadingState } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import {
  actorKindLabel,
  baseVersionLabel,
  classificationText,
  executionLabel,
  isReferralOverdue,
  operationLabel,
  originLabel,
  outcomeLabel,
  reasonCodeLabel,
  refusalLabel,
  statusLabel,
  statusTone,
} from './conflictPresentation';
import { CONFLICTS_PATH } from './conflictRoutes';

/**
 * Detail d'un conflit pour `support` : **metadonnees seulement** (ADR 0039,
 * point 5, « Roles »).
 *
 * Versions, classification, appareil **hache**, empreinte de la saisie refusee,
 * transitions et chronologie de l'entite. **Ce volet ne montre jamais le contenu
 * refuse**, meme conserve : le serveur ne le rend pas a ce role, et le texte du
 * volet le dit au lieu de laisser croire a un champ vide. Aucun bouton n'agit :
 * garder la version serveur, renvoyer ou classer sont des actions a part (#636).
 *
 * Une donnee absente s'ecrit « — » ; aucun nombre n'est calcule ici.
 */

const Row = ({ label, children }: { readonly label: string; readonly children: ReactNode }): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(96px, 36%) minmax(0, 1fr)',
      gap: 1.5,
      py: 1,
      borderBottom: 1,
      borderColor: 'tokens.border',
      '&:last-of-type': { borderBottom: 0 },
    }}
  >
    <Typography component="dt" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 14, fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }: { readonly children: ReactNode }): ReactElement => (
  <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

const Muted = ({ children }: { readonly children: ReactNode }): ReactElement => (
  <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>{children}</Typography>
);

const Absent = (): ReactElement => (
  <Box component="span" sx={{ color: 'tokens.textMuted', fontWeight: 400 }}>
    {ABSENT}
  </Box>
);

/** « 4 octobre 2026 à 10:42:07 », ou « — » pour une date absente ou illisible. */
const full = (iso: string | null): string => (iso === null ? ABSENT : (formatFull(iso) ?? ABSENT));

const Timeline = ({
  label,
  empty,
  children,
}: {
  readonly label: string;
  readonly empty: string;
  readonly children: readonly ReactElement[];
}): ReactElement =>
  children.length === 0 ? (
    <Muted>{empty}</Muted>
  ) : (
    <Box component="ul" aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Box>
  );

const TimelineItem = ({ title, detail, time }: { readonly title: string; readonly detail: string | null; readonly time: string }): ReactElement => (
  <Box
    component="li"
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 1, borderBottom: 1, borderColor: 'tokens.border', '&:last-of-type': { borderBottom: 0 } }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</Typography>
      {detail === null ? null : (
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>{detail}</Typography>
      )}
    </Box>
    <Typography sx={{ fontSize: 13, color: 'tokens.textMuted', flexShrink: 0 }}>{time}</Typography>
  </Box>
);

export interface ConflictDetailContentProps {
  readonly conflict: AdminSyncConflictDetail;
  /** Pose le filtre « ce compte » sur la liste (et referme le volet). */
  readonly onFilterAccount: (userId: string) => void;
}

export const ConflictDetailContent = ({ conflict, onFilterAccount }: ConflictDetailContentProps): ReactElement => {
  const classification = classificationText(conflict.classification);
  const outcome = outcomeLabel(conflict);
  const execution = executionLabel(conflict);

  return (
    <Box>
      <Box component="header" sx={{ mb: 1 }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, lineHeight: '24px', overflowWrap: 'anywhere' }}>
          {operationLabel(conflict.operationType)} refusée
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
          {entityTypeLabel(conflict.entityType)} · {conflict.email}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          <Badge label={statusLabel(conflict.status)} tone={statusTone(conflict.status)} />
          <Badge label={classification.label} tone={classification.tone} />
          <Badge label={classification.nature} tone="neutral" />
        </Box>
      </Box>

      <Box component="dl" sx={{ m: 0 }}>
        <Row label="Compte">
          <Link to={accountDetailPath(conflict.userId)} aria-label={`Ouvrir la fiche de ${conflict.email}`}>
            {conflict.email}
          </Link>
        </Row>
        <Row label="Enregistrement">
          <TechnicalId value={conflict.entityId} label="l’identifiant de l’enregistrement" />
        </Row>
        <Row label="Cause du refus">{refusalLabel(conflict.reason)}</Row>
        <Row label="Versions">
          Départ : {baseVersionLabel(conflict.baseVersion)} · serveur : version {conflict.serverVersion}
        </Row>
        <Row label="Détecté">{full(conflict.detectedAt)}</Row>
        <Row label="Dernière détection">
          {full(conflict.lastDetectedAt)}
          {conflict.detectionCount > 1 ? ` · refusé ${String(conflict.detectionCount)} fois` : ''}
        </Row>
        <Row label="Origine">{originLabel(conflict.origin)}</Row>
        <Row label="Appareil">
          {conflict.deviceHash === null ? (
            <Absent />
          ) : (
            <Box component="span" sx={{ fontFamily: fontStacks.mono, fontWeight: 400 }} title="Empreinte de l’identifiant d’appareil : elle se compare, elle ne se lit pas">
              {conflict.deviceHash}
            </Box>
          )}
        </Row>
        <Row label="Application">{conflict.appVersion ?? <Absent />}</Row>
        <Row label="Empreinte de la saisie">
          <TechnicalId value={conflict.payloadHash} label="l’empreinte de la saisie refusée" abbreviated />
        </Row>
        <Row label="Événement du serveur">{conflict.serverEventId ?? <Absent />}</Row>
        {conflict.parentConflictId === null ? null : (
          <Row label="Conflit précédent">
            <Link to={`${CONFLICTS_PATH}/${encodeURIComponent(conflict.parentConflictId)}`}>Ouvrir le conflit précédent</Link>
          </Row>
        )}
      </Box>

      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted', mt: 1 }}>{classification.explanation}</Typography>

      <SectionTitle>Issue</SectionTitle>
      {conflict.status === 'ouvert' ? (
        <Muted>Aucune décision n’a été prise.</Muted>
      ) : conflict.status === 'renvoye_utilisatrice' ? (
        <Muted>
          {isReferralOverdue(conflict, new Date())
            ? `Sans réponse : la décision a été renvoyée à l’utilisatrice et son échéance est passée depuis le ${full(conflict.referralExpiresAt)}. Le conflit reste ouvert.`
            : `La décision est renvoyée à l’utilisatrice${conflict.referralExpiresAt === null ? '' : `, jusqu’au ${full(conflict.referralExpiresAt)}`}.`}
        </Muted>
      ) : (
        <Box component="dl" sx={{ m: 0 }}>
          <Row label="État">{statusLabel(conflict.status)}</Row>
          {outcome === null ? null : <Row label="Décision">{outcome}</Row>}
          {execution === null ? null : <Row label="Exécution">{execution}</Row>}
          {conflict.reasonCode === null ? null : <Row label="Motif">{reasonCodeLabel(conflict.reasonCode)}</Row>}
          <Row label="Tranché le">{full(conflict.resolvedAt)}</Row>
          <Row label="Suivi clos le">{full(conflict.closedAt)}</Row>
        </Box>
      )}

      <SectionTitle>Version refusée</SectionTitle>
      <Muted>
        {conflict.versionAvailable
          ? `Une version est encore conservée, jusqu’au ${full(conflict.versionPurgeAt)}. Cet écran ne la montre jamais, même conservée.`
          : 'Aucune version n’est disponible : elle n’a pas été conservée, a été purgée, ou le conflit vient d’une reprise de l’historique.'}
      </Muted>

      <SectionTitle>Historique du conflit</SectionTitle>
      <Timeline label="Transitions du conflit" empty="Aucune transition enregistrée.">
        {conflict.transitions.map((transition, index) => (
          <TimelineItem
            key={`${transition.occurredAt}:${String(index)}`}
            title={
              transition.fromStatus === transition.toStatus
                ? reasonCodeLabel(transition.reasonCode)
                : `${transition.fromStatus === null ? 'Détecté' : statusLabel(transition.fromStatus)} → ${statusLabel(transition.toStatus)}`
            }
            detail={[
              actorKindLabel(transition.actorKind),
              transition.fromStatus === transition.toStatus ? null : reasonCodeLabel(transition.reasonCode),
              transition.proofEventId === null ? null : `événement n° ${String(transition.proofEventId)}`,
              transition.abandonedKeyCount === 0
                ? null
                : `${String(transition.abandonedKeyCount)} saisie${transition.abandonedKeyCount > 1 ? 's' : ''} abandonnée${transition.abandonedKeyCount > 1 ? 's' : ''}`,
            ]
              .filter((part): part is string => part !== null)
              .join(' · ')}
            time={formatInstant(transition.occurredAt)}
          />
        ))}
      </Timeline>

      <SectionTitle>Chronologie de l’enregistrement</SectionTitle>
      <Timeline label="Versions de l’enregistrement au journal" empty="Aucune version de cet enregistrement au journal.">
        {conflict.entityEvents.map((event) => (
          <TimelineItem
            key={event.eventId}
            title={`Version ${String(event.version)} · ${operationLabel(event.operationType)}`}
            detail={`${event.byThisAccount ? 'Ce compte' : 'Un autre compte'}${event.deviceHash === null ? '' : ` · appareil ${event.deviceHash}`}`}
            time={formatInstant(event.occurredAt)}
          />
        ))}
      </Timeline>
      <Typography sx={{ mt: 1, fontSize: 12, color: 'tokens.textMuted' }}>
        {conflict.entityEventsTruncated
          ? 'Les 50 versions les plus récentes, de la plus ancienne à la plus récente : les plus anciennes ne sont pas affichées. Le contenu des versions n’est jamais montré.'
          : 'Du plus ancien au plus récent. Le contenu des versions n’est jamais montré.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
        <Button
          size="S"
          onClick={() => {
            onFilterAccount(conflict.userId);
          }}
        >
          Voir les conflits de ce compte
        </Button>
        <Button size="S" to={`${SYNC_SUPERVISION_PATH}?${new URLSearchParams({ userId: conflict.userId }).toString()}`}>
          Voir ses tentatives de synchronisation
        </Button>
      </Box>
    </Box>
  );
};

export interface ConflictDetailPanelProps extends Pick<ConflictDetailContentProps, 'onFilterAccount'> {
  readonly conflictId: string;
  /** Jeton de relecture : « Actualiser » et le retour sur l'onglet le font avancer. */
  readonly token: number;
  readonly onRetry: () => void;
}

export const ConflictDetailPanel = ({ conflictId, token, onRetry, onFilterAccount }: ConflictDetailPanelProps): ReactElement => {
  const http = useHttpClient();
  const { data, failure } = useAdminQuery(`conflict:${conflictId}`, () => createAdminApi(http).getSyncConflict(conflictId), { token });

  if (failure?.notFound === true) {
    return (
      <EmptyState
        title="Conflit introuvable"
        description="Aucun conflit du registre ne porte cet identifiant, ou son compte a été supprimé ou anonymisé : le serveur n’en rend rien."
      />
    );
  }

  if (failure !== null && data === null) {
    return failure.forbidden ? (
      <AccessDenied title="Votre rôle ne permet pas de consulter ce conflit">
        Votre compte reste connecté : il manque le rôle d’administration requis.
      </AccessDenied>
    ) : (
      <ErrorState message={failure.message} onRetry={onRetry} />
    );
  }

  if (data === null) {
    return <LoadingState label="Chargement du conflit…" />;
  }

  return (
    <>
      {failure === null ? null : <ErrorState message={failure.message} onRetry={onRetry} />}
      <ConflictDetailContent conflict={data} onFilterAccount={onFilterAccount} />
    </>
  );
};
